/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/jsx-key */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Divider, Typography, DialogActions } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  meetingColor,
  timeZoneWiseTime,
  formattedTimeSlot,
  timeZoneAbbreviation,
  createZoomMeetingUrl,
  timeZonWiseDateTime,
  checkCallType
} from '../../components/common';
import { API } from '../../../../api';
import { toast } from 'react-toastify';
import CalendarLoader from './CalendarLoader';
import '../../../../assets/style/pagination.css';
import PostCallPopup from '../chat/PostCallPopup';
import CallBookingModal from '../CallBookingModal';
import { callActionFlag } from './CalendarFunctions';
import RejectionCallModal from './RejectionCallModal';
import MySkeleton from '../../../../components/Skeleton';
import MyPagination from '../../components/MyPagination';
import CancelationCallModal from './CancelationCallModal';
import { getUserDetails } from '../../../../utils/orgName';
import {
  openMeetingWithRedirect,
  MEETING_REVIEW_EVENT,
  MEETING_REVIEW_STORAGE_KEY
} from '../../../../utils/openMeetingWithRedirect';
import { getProgramContext } from '../../../../hooks/useProgramContext';
import { BooleanLiteralTypeAnnotation } from '@babel/types';
import SeeBookingDetailsModal from './SeeBookingDetailsModal';
import useCallDetails from '../../../../queries/useCallDetails';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchCallsCount } from '../../../../redux/slices/callCountSlice';
import useResponsiveScreenSwitch from '../../../../utils/useResponsiveScreenSwitch';
import { fetchCommunityMembers } from '../../../../redux/slices/acs-community-members/acsCommunityMembers';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

/**
 * Phone layout (the custom.scss rules stay as they are for desktop). The page
 * grows with its content so the app's own scroll area scrolls it; the header
 * reads "N Meetings" + Schedule on one row with the filter chips as one swipeable
 * row under them; each meeting is a full-width card with its title wrapping and
 * its buttons side by side. Nested under the root class to outrank custom.scss.
 */
const phoneCalendarSx = {
  [MOBILE_SX]: {
    '&.CalendarPageContainer': {
      height: 'auto',
      overflow: 'visible',
      padding: '0 !important',
      background: 'transparent !important'
    },
    '& .CalendarCard': { borderRadius: '8px', overflow: 'hidden' },
    '& .CallsPageHeader': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      alignItems: 'center',
      columnGap: '12px',
      rowGap: '12px',
      padding: '12px'
    },
    // The legend and the actions hand their children to the grid above.
    '& .CallsLegend, & .CallsHeaderActions': { display: 'contents' },
    '& .CallsLegendDivider': { display: 'none' },
    '& .CallsLegendCount': { gridRow: 1, gridColumn: 1, fontSize: '16px', color: '#FDFDFD !important' },
    '& .CallsScheduleBtn': { gridRow: 1, gridColumn: 2, minHeight: '40px' },
    '& .CallsHeaderActions > :not(.CallsScheduleBtn)': { gridRow: 3, gridColumn: '1 / -1', justifySelf: 'center' },
    '& .CallsLegendFilters': {
      gridRow: 2,
      gridColumn: '1 / -1',
      flexWrap: 'nowrap',
      gap: '8px',
      overflowX: 'auto',
      margin: '0 -12px',
      padding: '0 12px',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' }
    },
    '& .CallsLegendFiltersLabel': { flexShrink: 0, fontSize: '13px' },
    '& .CallsLegendChip': {
      flexShrink: 0,
      minHeight: '36px',
      padding: '0 14px',
      fontSize: '13px',
      whiteSpace: 'nowrap'
    },
    '& .CallsTableList': { gridTemplateColumns: 'minmax(0, 1fr)', gap: '12px', padding: '12px' },
    '& .MeetingCard': { padding: '12px', gap: '12px' },
    '& .MeetingCardMain': { alignItems: 'flex-start', gap: '12px' },
    '& .MeetingCardDetails': { gap: '6px' },
    '& .MeetingCardTitle': { whiteSpace: 'normal', overflowWrap: 'anywhere', fontSize: '16px', lineHeight: 1.3 },
    '& .MeetingCardMeta': { gap: '6px 10px' },
    // Who the meeting is with gets its own line instead of a divider left hanging at a line end.
    '& .MeetingCardMetaDivider:nth-of-type(2)': { display: 'none' },
    '& .MeetingCardMetaDivider:nth-of-type(2) + .MeetingCardMetaText': { flexBasis: '100%' },
    '& .MeetingCardMetaText': { fontSize: '13px' },
    '& .MeetingCardCtas': { width: '100%', flexDirection: 'row', gap: '8px' },
    '& .MeetingCardCtas > .MuiBox-root': { flex: 1, flexDirection: 'row', alignItems: 'stretch', gap: '8px' },
    '& .MeetingCardCtas > .MuiBox-root > *': { flex: 1 },
    '& .MeetingCardCtas .MuiButton-root': {
      width: '100%',
      minHeight: '40px',
      fontSize: '14px !important'
    }
  }
};

export interface CallRowData {
  meetingId: string;
  _id: string;
  callDate: string;
  callTime: string;
  callTitle: string;
  callTimeInterval: number;
  participantsUser: {
    mid: string;
    callRejectStatus: boolean;
    isCallJoined: boolean; // Add the isCallJoined property
    voteFlag: boolean; // Add the voteFlag property
  }[];
  chatType?: string;
  callBookedWith?: {
    id: string;
    displayName: string;
    headshot: string;
  };
  callBookedBy?: {
    id: string;
    displayName: string;
    headshot: string;
  };
  description: string;
  callStatus: string;
  meetingType: string;
  objective?: any;
  /** From BE getScheduleCall: true when the call is completed and the user has not yet submitted a review */
  pendingReview?: boolean;
}

const Calendar = () => {
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.userProfile.data);
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const isAdmin = selectedGroup?.role === 'admin';
  const timezone = userData?.personal_details?.timezone || '';
  const { userId } = getUserDetails();
  const userProfileDetails = useProfileDetails();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const userCallsData = useCallDetails(page, pageSize);
  const allTotalObjects = userCallsData.allTotelCount;
  const { forMobile } = useResponsiveScreenSwitch();
  const [isCallEditing, setIsCallEditing] = useState(false);
  const meetingFilters = useMemo(() => meetingColor(), []);
  const [activeFilters, setActiveFilters] = useState<string[]>(() => meetingFilters.map((filter) => filter.value));
  const toggleFilter = (value: string) => {
    setActiveFilters((prev) => {
      if (prev.includes(value)) {
        return prev.filter((filter) => filter !== value);
      }
      return [...prev, value];
    });
  };

  useEffect(() => {
    if (page !== 0) {
      userCallsData.refetch();
    }
  }, [page]);

  const totalPages = Math.ceil(allTotalObjects / pageSize);

  const handlePageChange = (newPage: any) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const callRows: CallRowData[] = userCallsData?.data || [];
  // Live view of callRows for the mount-time review-signal listener below.
  const callRowsRef = useRef<CallRowData[]>(callRows);
  callRowsRef.current = callRows;
  const [callMeetingId, setMetingId] = useState();
  const [cancelCallMeetingId, setcancelCallMeetingId] = useState();
  const [rejectCallMeetingId, setrejectCallMeetingId] = useState();
  const [openPostCallPopup, setOpenPostCallPopup] = useState(true);
  const [reviewMeetingId, setReviewMeetingId] = useState<string | null>(null);
  const loading = userCallsData.isLoading; // Loading

  /** Open Rejection Reason modal */
  const [openRejectionCall, setRejectionCallOpen] = useState(false);
  const handleRejectionCall = (meetingId: any, type: string) => {
    setMetingId(meetingId);
    setRejectionCallOpen(true);
    if (type === 'modal') {
      setopenCallbookingDetailsModal(false);
    }
  };

  const handleCallCancellation = () => {
    setcancelCallMeetingId(callMeetingId);
  };

  const closeRejectionModal = () => {
    userCallsData.refetch();
    setRejectionCallOpen(false);
  };

  const handleClosePostCallPopup = () => {
    userCallsData.refetch();
    setOpenPostCallPopup(false);
  };

  /** Open Call Cancelation modal */
  const [openCancelationCall, setCancelationCallOpen] = useState(false);
  const handleCancelationCall = (meetingId: any, type: string) => {
    setMetingId(meetingId);
    setCancelationCallOpen(true);
    if (type === 'modal') {
      setopenCallbookingDetailsModal(false);
    }
  };
  const handleCallRejection = () => {
    setrejectCallMeetingId(callMeetingId);
  };
  const closeCancelModal = () => {
    userCallsData.refetch();
    setCancelationCallOpen(false);
  };

  /** See Booking Details Modal */
  const acsChannelParticipants = useAppSelector((state) => state.acsChannelParticipants.data);
  // const allReviewMeetingId: any[] = [];
  const [openCallbookingDetailsModal, setopenCallbookingDetailsModal] = useState(false);
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const [currentMeetingIndex, setCurrentMeetingIndex] = useState(0);
  const [allattendeeDetails, setallAttendeeDetails] = useState([]);
  const [bookedCallDetails, setBookedCallDetails] = useState<CallRowData[]>([]);
  const handleSeeBookingDetails = (attendeeDetails: any, callId: any) => {
    const filteredData: CallRowData[] = callRows?.filter((item: { _id: string }) => item._id === callId);
    const filteredMidValues: any[] = filteredData
      ?.map((item: CallRowData) => {
        if (item.participantsUser.some((participant) => participant.mid === userId)) {
          if (item?.chatType === 'Chats') {
            return [item?.callBookedBy?.id, item?.callBookedWith?.id];
          } else {
            return [item?.callBookedBy?.id];
          }
        } else {
          return [item?.callBookedBy?.id];
        }
      })
      .flat();
    const filteredattendeeDetails = attendeeDetails.filter(
      (item: { mid: any }) => !filteredMidValues.includes(item.mid)
    );
    setBookedCallDetails(filteredData);
    setallAttendeeDetails(filteredattendeeDetails);
    setopenCallbookingDetailsModal(true);
  };

  const closeSeeBookingDetailsModal = () => {
    userCallsData.refetch();
    setopenCallbookingDetailsModal(false);
  };

  const getChannels = () => {
    const ctx = getProgramContext();
    dispatch(
      fetchCommunityMembers({
        orgId: ctx.orgId,
        location: ctx.location
      })
    );
  };

  useEffect(() => {
    dispatch(fetchCallsCount(getProgramContext().location));
    getChannels();
  }, []);
  useEffect(() => {
    userCallsData.refetch();
  }, []);

  const attendeesImage = (participantsUserRow: { headshot: string }[], callId: any) => {
    const imageSources = participantsUserRow.slice(0, 3).map((attendee) => attendee?.headshot);
    return (
      <Box display="flex" gap="8px" alignItems="center">
        {participantsUserRow.length > 2 && (
          <>
            <Box className="CallsAttendeesImages">
              {imageSources.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`Attendee ${index + 1}`}
                  className={`Image${index + 1} cursor-pointer`}
                  onClick={() => handleSeeBookingDetails(participantsUserRow, callId)}
                />
              ))}
            </Box>
            <Typography
              variant="h6"
              className="cursor-pointer"
              color="#0071A9"
              onClick={() => handleSeeBookingDetails(participantsUserRow, callId)}
              noWrap
            >
              See all attendees
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const rejectCancelCallButton = (callsData: any, type: string) => {
    const isUserInParticipants = callsData.participantsUser?.some(
      (participant: { mid: string }) => participant.mid === userId
    );

    if (!isUserInParticipants) {
      return null; // Do not render anything if the member is not in participantsUser
    }

    let flag = 0;
    if (callsData?.callBookedBy?.id === userId) {
      flag = 1;
    }
    const meetingId = callsData?.meetingId;

    return (
      <>
        {flag === 1 && meetingId !== cancelCallMeetingId && (
          <Button
            variant="outlined"
            className={type === 'page' ? 'CallsPageBtn MeetingCancelButton' : ''}
            color={type === 'page' ? 'error' : 'secondary'}
            fullWidth={type !== 'page'}
            onClick={() => handleCancelationCall(meetingId, type)}
          >
            Cancel
          </Button>
        )}

        {flag !== 1 && meetingId !== rejectCallMeetingId && (
          <Button
            variant="outlined"
            className={type === 'page' ? 'CallsPageBtn MeetingCancelButton' : ''}
            color={type === 'page' ? 'error' : 'secondary'}
            fullWidth={type !== 'page'}
            onClick={() => handleRejectionCall(meetingId, type)}
          >
            Reject
          </Button>
        )}
      </>
    );
  };

  const joinCallButton = (callsData: any, type: string) => {
    const meetingId = callsData?.meetingId;
    const isUserInParticipants = isUserInParticipantsArray(callsData);
    const callStartTimestamp = new Date(callsData.callDate + 'T' + callsData.callTime + 'Z').getTime();
    const currentTimestamp = new Date().getTime();
    const tenMinutesBefore = callStartTimestamp - 10 * 60 * 1000;
    const isWithinJoinWindow = currentTimestamp >= tenMinutesBefore;

    if (meetingId !== cancelCallMeetingId && meetingId !== rejectCallMeetingId && isUserInParticipants) {
      return (
        <Button
          className={type === 'page' ? 'CallsPageBtn MeetingJoinButton' : ''}
          variant="contained"
          color="primary"
          fullWidth={type !== 'page'}
          key={callsData?._id}
          disabled={false}
          onClick={() =>
            handleGoToCallIconClick(
              callsData?.callTimeInterval,
              callsData?.callDate,
              callsData?.callTime,
              callsData?.meetingId,
              isRejoin(callsData),
              callsData?.callBookedBy,
              callsData?.meetingType,
              callsData?.meetingLink
            )
          }
        >
          {isRejoin(callsData) ? 'Rejoin' : 'Join'}
        </Button>
      );
    } else {
      // Return null or an empty fragment if the member is not in the participants array
      return null;
    }
  };

  const isUserInParticipantsArray = (callsData: any) => {
    const userId = getUserDetails().userId;
    return callsData.participantsUser?.some((user: { mid: string }) => user.mid === userId);
  };

  const isRejoin = (callsData: any) => {
    const data = callsData.participantsUser?.find((user: { mid: string }) => user.mid === getUserDetails().userId);
    return data ? data.isCallJoined : false;
    // true => go to call
    // false => rejoin
  };

  const handleGoToCallIconClick = (
    callTimeInterval: number,
    callDate: string,
    callTime: string,
    meetingId: string,
    isRejoin: BooleanLiteralTypeAnnotation,
    callBookedBy: any,
    meetingType: string,
    meetingLink: string
  ) => {
    const baseUrl = window.location.origin;
    // call start time
    const callStartTimestamp = new Date(callDate + 'T' + callTime + 'Z').getTime();
    const callStartUnixTime = Math.floor(callStartTimestamp / 1000);
    const currentTimestamp = new Date().getTime();
    const currentcallStartUnixTime = Math.floor(currentTimestamp / 1000);
    const currentUnixTime = Math.floor(currentTimestamp / 1000);
    const callEndUnixTime = currentUnixTime + callTimeInterval * 60;

    if (currentUnixTime + 300 >= callStartUnixTime) {
      // if current time is greater than call time
      if (currentUnixTime > callEndUnixTime) {
        toast.error('This meeting has already taken place');
      } else {
        if (!isRejoin) {
          isJoinCall(meetingId);
        }
        if (meetingType === 'Dosen') {
          const url = createZoomMeetingUrl(meetingId, callDate, callTime, callTimeInterval, callBookedBy);
          openMeetingWithRedirect(url, { meetingId, meetingType, callDate, callTime, callTimeInterval });
        } else {
          openMeetingWithRedirect(meetingLink, { meetingId, meetingType, callDate, callTime, callTimeInterval });
        }
      }
    } else {
      toast.warning('You can join the meeting from 5 minutes before the meeting time');
    }
  };

  const isJoinCall = async (meetingId: string) => {
    const ctx = getProgramContext();
    try {
      const response = await API.rejoinCallUpdateFlag(
        getUserDetails().userId,
        meetingId,
        ctx.orgId,
        ctx.location
      );
    } catch (e) {}
  };

  const ModalDialogActions: React.FC<{ callrow: CallRowData[] }> = ({ callrow }) => {
    if (callrow[0].meetingType === 'InPerson') {
      return null;
    }
    return (
      <DialogActions>
        {callActionFlag(callrow[0], getUserDetails()?.userId) === 1 ? (
          rejectCancelCallButton(callrow[0], 'modal')
        ) : (
          <span />
        )}
        {callActionFlag(callrow[0], getUserDetails()?.userId) === 1 ? joinCallButton(callrow[0], 'modal') : <span />}
      </DialogActions>
    );
  };

  const userCallsDataRefetch = () => {
    userCallsData.refetch();
  };

  const getCallDateParts = (callRowData: CallRowData) => {
    if (!callRowData?.callDate || !callRowData?.callTime || !timezone) {
      return { month: '', day: '', weekday: '' };
    }
    const locale = 'en-US';
    const dateTime = timeZonWiseDateTime(
      callRowData.callDate.toString(),
      callRowData.callTime.toString(),
      timezone
    );

    return {
      month: dateTime.toLocaleString(locale, { month: 'short' }),
      day: dateTime.getDate().toString(),
      weekday: dateTime.toLocaleString(locale, { weekday: 'short' })
    };
  };

  const getCallStatusData = (callRowData: CallRowData) => {
    const matchingParticipant = callRowData.participantsUser?.find((user) => user.mid === userId);
    const joinedParticipantsCount =
      callRowData.participantsUser?.filter((participant) => participant.isCallJoined)?.length || 0;
    const isBookerJoined =
      callRowData.participantsUser?.some((participant) => participant.mid === callRowData?.callBookedBy?.id) || false;

    const callTypeData = checkCallType(
      callRowData.callStatus,
      callRowData.callDate,
      callRowData.callTime,
      callRowData.callTimeInterval,
      matchingParticipant?.callRejectStatus,
      joinedParticipantsCount,
      isBookerJoined
    );

    const filterMeta =
      meetingFilters.find((meeting) => meeting.color === callTypeData?.color_code) ||
      meetingFilters.find((meeting) => meeting.title === callTypeData?.title) ||
      meetingFilters[0];

    return {
      color: callTypeData?.color_code || filterMeta?.color || '#00972D',
      value: filterMeta?.value || 'Upcoming',
      label: filterMeta?.title.replace(' meeting', '') || 'Upcoming'
    };
  };

  const getMeetingTimeLabel = (callRowData: CallRowData) => {
    if (!callRowData?.callDate || !callRowData?.callTime || !timezone) {
      return '';
    }

    return `${timeZoneWiseTime(
      callRowData.callDate.toString(),
      callRowData.callTime.toString(),
      timezone
    ).toLowerCase()} (${timeZoneAbbreviation(timezone)})`;
  };

  const isCallCompleted = (callRow: CallRowData, hasJoined: boolean) => {
    const status = (callRow?.callStatus || '').toString().toLowerCase();
    if (status.includes('complete')) return true;
    if (hasJoined && !status.includes('cancel') && !status.includes('reject')) return true;
    const callStartTimestamp = new Date(`${callRow.callDate}T${callRow.callTime}Z`).getTime();
    const callEndTimestamp = callStartTimestamp + (callRow.callTimeInterval || 0) * 60 * 1000;
    return Number.isFinite(callEndTimestamp) && Date.now() > callEndTimestamp;
  };

  /**
   * Gmeet rows confirmed over by the attendance API (`meetingEnded` — everyone
   * left the room). Lets the auto-popup below show for a Google Meet call that
   * ended early, before its scheduled slot is up.
   */
  const [endedGmeetMeetingIds, setEndedGmeetMeetingIds] = useState<string[]>([]);
  const checkedGmeetIdsRef = useRef<Set<string>>(new Set());

  const markGmeetEndedIfEveryoneLeft = (meetingId: string) => {
    const { orgId, location } = getUserDetails();
    API.checkGoogleMeetAttendance(meetingId, orgId, location)
      .then((res: any) => {
        if (res?.data?.meetingEnded === true) {
          setEndedGmeetMeetingIds((prev) => [...prev, meetingId]);
        }
      })
      .catch(() => {
        // 404 / network — the slot-end rule still surfaces the prompt later.
      });
  };

  useEffect(() => {
    if (isAdmin) return;
    (callRows || []).forEach((callRow: CallRowData) => {
      if (callRow.meetingType !== 'Gmeet') return;
      if (String(callRow?.callStatus || '').toLowerCase() === 'canceled') return;
      if (callRow.pendingReview !== true) return;
      const myParticipant = callRow.participantsUser?.find((p) => p.mid === userId);
      if (myParticipant?.voteFlag === true || myParticipant?.isCallJoined !== true) return;
      const callStart = new Date(`${callRow.callDate}T${callRow.callTime}Z`).getTime();
      const callEnd = callStart + (callRow.callTimeInterval || 0) * 60 * 1000;
      if (!Number.isFinite(callEnd) || Date.now() > callEnd) return; // slot over — no API check needed
      if (checkedGmeetIdsRef.current.has(callRow.meetingId)) return;
      checkedGmeetIdsRef.current.add(callRow.meetingId);
      markGmeetEndedIfEveryoneLeft(callRow.meetingId);
    });
  }, [callRows, isAdmin, userId]);

  /** Review modal: use BE pendingReview from getScheduleCall. Show modal only for completed + pendingReview === true; ignore callStatus === "Canceled". Never show for admin. */
  const filteredCallRows: CallRowData[] = useMemo(() => {
    if (isAdmin) return [];
    return (callRows || []).filter((callRow: CallRowData) => {
      const status = String(callRow?.callStatus || '').toLowerCase();
      if (status === 'canceled') return false;
      if (callRow.pendingReview !== true) return false;
      // Don't re-prompt a user who already submitted their own review for this
      // call, even if the BE still reports pendingReview (it can stay true until
      // every participant has reviewed).
      const myParticipant = callRow.participantsUser?.find((p) => p.mid === userId);
      if (myParticipant?.voteFlag === true) return false;
      // Never prompt someone who hasn't been in the call while the meeting
      // window is still open (pendingReview flips at the start time, which used
      // to surface the popup before the user even joined). Prompt once they have
      // joined (leaving the call routes back here) or once the slot has ended.
      const iJoined = myParticipant?.isCallJoined === true;
      const callStart = new Date(`${callRow.callDate}T${callRow.callTime}Z`).getTime();
      const callEnd = callStart + (callRow.callTimeInterval || 0) * 60 * 1000;
      const windowOver = Number.isFinite(callEnd) && Date.now() > callEnd;
      // Google Meet: only prompt once the slot ended or the attendance API
      // confirmed everyone left — having joined isn't enough, the other
      // participant may still be in the room.
      if (callRow.meetingType === 'Gmeet') {
        return windowOver || endedGmeetMeetingIds.includes(callRow.meetingId);
      }
      return iJoined || windowOver;
    });
  }, [callRows, isAdmin, userId, endedGmeetMeetingIds]);

  const openNextPopup = () => {
    if (currentMeetingIndex < filteredCallRows.length - 1) {
      setCurrentMeetingIndex(currentMeetingIndex + 1);
    } else {
      setOpenPostCallPopup(false);
      setCurrentMeetingIndex(0);
      userCallsData.refetch();
    }
  };

  const handleOpenReviewPopup = (meetingId: string) => {
    setReviewMeetingId(meetingId);
    setOpenPostCallPopup(true);
  };

  const handleCloseReviewPopup = () => {
    setOpenPostCallPopup(false);
    setReviewMeetingId(null);
    userCallsData.refetch();
  };

  /**
   * When the user closes a meeting tab, `openMeetingWithRedirect` brings them
   * back here and asks us to open the review modal for that meeting — via a
   * window event (already on this page) or sessionStorage (arrived via redirect).
   */
  useEffect(() => {
    const consumeReviewSignal = (meetingId?: string | null) => {
      if (!meetingId) return;
      try {
        window.sessionStorage.removeItem(MEETING_REVIEW_STORAGE_KEY);
      } catch {
        /* sessionStorage may be unavailable */
      }
      // A Gmeet review signal can arrive minutes after the tab closed (it waits
      // for everyone to leave / the slot to end) — the user may have already
      // reviewed via the Review button in the meantime. Skip if so. Ref, not
      // state: this closure is created once on mount.
      const row = callRowsRef.current.find((r) => r.meetingId === meetingId || r._id === meetingId);
      const mine = row?.participantsUser?.find((p) => p.mid === userId);
      if (mine?.voteFlag === true) return;
      // Refresh so the just-finished meeting is present, then open its review.
      userCallsData.refetch();
      handleOpenReviewPopup(meetingId);
    };

    let stored: string | null = null;
    try {
      stored = window.sessionStorage.getItem(MEETING_REVIEW_STORAGE_KEY);
    } catch {
      /* sessionStorage may be unavailable */
    }
    if (stored) consumeReviewSignal(stored);

    const onReviewEvent = (e: Event) => {
      consumeReviewSignal((e as CustomEvent<{ meetingId?: string }>).detail?.meetingId);
    };
    window.addEventListener(MEETING_REVIEW_EVENT, onReviewEvent);
    return () => window.removeEventListener(MEETING_REVIEW_EVENT, onReviewEvent);
  }, []);

  /** The call row targeted by a single-meeting review (Review button / tab-close redirect). */
  const reviewRow = reviewMeetingId
    ? callRows.find((row) => row.meetingId === reviewMeetingId || row._id === reviewMeetingId)
    : undefined;

  const handleCalendar = (flag: boolean) => setCalendarOpen(flag);

  const callWithTitle = (callRow: any) => {
    let reviewModalTitle = '';
    if (callRow?.chatType === 'Chats') {
      // p2p
      reviewModalTitle =
        userId === callRow?.callBookedWith?.id
          ? callRow?.callBookedBy?.displayName
          : callRow?.callBookedWith?.displayName;
    } else {
      // group
      reviewModalTitle = callRow?.callBookedWith?.name; // group name
    }
    return reviewModalTitle;
  };

  const renderNoMeetingsMessage = () => (
    <Box p={2} className="CallsTableEmptyState">
      <Typography textAlign="center">No meetings in your calendar yet</Typography>
    </Box>
  );

  const filteredMeetings = useMemo(() => {
    if (!userCallsData?.data || activeFilters.length === 0) {
      return [];
    }
    return userCallsData.data.filter((item: CallRowData) => activeFilters.includes(getCallStatusData(item).value));
  }, [userCallsData?.data, activeFilters, timezone, userId]);

  return (
    <Box className="bodyBox CalendarPageContainer" p={{ xs: 0, sm: 2 }} sx={phoneCalendarSx}>
      <Box className="CardBox CalendarCard">
        <Box className="CallsPageHeader">
          <Box className="CallsLegend">
            {loading ? (
              <MySkeleton variant="text" height={20} width={100} />
            ) : (
              <Typography className="CallsLegendCount">{`${filteredMeetings.length} Meetings`}</Typography>
            )}
            <Box className="CallsLegendDivider" />
            <Box className="CallsLegendFilters">
              <Typography className="CallsLegendFiltersLabel">Filters:</Typography>
              {meetingFilters.map((meeting, index) => {
                const label = meeting.title.replace(' meeting', '');
                const isActive = activeFilters.includes(meeting.value);
                return (
                  <button
                    type="button"
                    key={index}
                    className={`CallsLegendChip ${isActive ? 'is-active' : ''}`}
                    style={isActive ? { backgroundColor: meeting.color } : undefined}
                    onClick={() => toggleFilter(meeting.value)}
                  >
                    {label}
                  </button>
                );
              })}
            </Box>
          </Box>
          {!loading && (
            <Box className="CallsHeaderActions">
              {allTotalObjects > 10 && (
                <MyPagination
                  counts={[{ count: totalPages }]}
                  currentPage={page}
                  onPageChange={handlePageChange}
                  allTotalObjects={allTotalObjects}
                  pageSize={pageSize}
                />
              )}
              <Button
                variant="contained"
                color="primary"
                className="CallsScheduleBtn"
                id="addScheduleNewMeetingIcon"
                data-testid="calendar-new-meeting-button"
                onClick={() => handleCalendar(true)}
              >
                Schedule Meeting
              </Button>
            </Box>
          )}
        </Box>
        <Divider />

        <Box className="CallsTable">
          <Box className="CallsTableList">
            {loading
              ? Array.from({ length: 5 }).map((_, index) => <CalendarLoader key={index} />)
              : userCallsData.error || filteredMeetings.length === 0
              ? renderNoMeetingsMessage()
              : filteredMeetings.map((item: CallRowData) => {
                  const { month, day, weekday } = getCallDateParts(item);
                  const statusData = getCallStatusData(item);
                  const meetingTimeLabel = getMeetingTimeLabel(item);
                  const durationLabel =
                    typeof item?.callTimeInterval === 'number'
                      ? formattedTimeSlot(item.callTimeInterval).toLowerCase()
                      : '';
                  const attendeeLabel = callWithTitle(item);
                  const actionFlag = callActionFlag(item, userId);
                  const handleDetailsClick = () => handleSeeBookingDetails(item?.participantsUser, item?._id);
                  const matchingParticipant = item.participantsUser.find((user) => user.mid === userId);
                  const isCompleted = Boolean(
                    matchingParticipant && isCallCompleted(item, matchingParticipant?.isCallJoined === true)
                  );
                  const hasReview = matchingParticipant?.voteFlag === true;

                  return (
                    <Box className="CallsTableBox" key={item?._id} data-testid="calendar-event">
                      <Box className="MeetingCard">
                        <Box className="MeetingCardMain" onClick={handleDetailsClick}>
                          <Box className="MeetingCardDate">
                            <Box className="MeetingCardDateMonth" sx={{ backgroundColor: statusData.color }}>
                              <Typography sx={{color: 'white'}}>{month}</Typography>
                            </Box>
                            <Box className="MeetingCardDateDay">
                              <Typography className="MeetingCardDateNumber">{day}</Typography>
                              <Typography className="MeetingCardDateWeekday">{weekday}</Typography>
                            </Box>
                          </Box>
                          <Box className="MeetingCardDetails">
                            <Typography variant="h5" className="MeetingCardTitle">
                              {item?.callTitle}
                            </Typography>
                            <Box className="MeetingCardMeta">
                              {meetingTimeLabel && (
                                <Box className="MeetingCardMetaItem">
                                  <AccessTimeIcon className="MeetingCardMetaClock" fontSize="inherit" />
                                  <Typography className="MeetingCardMetaText">{meetingTimeLabel}</Typography>
                                </Box>
                              )}
                              {durationLabel && (
                                <>
                                  <span className="MeetingCardMetaDivider" />
                                  <Typography className="MeetingCardMetaText">{durationLabel}</Typography>
                                </>
                              )}
                              {attendeeLabel && (
                                <>
                                  <span className="MeetingCardMetaDivider" />
                                  <Typography className="MeetingCardMetaText">{attendeeLabel}</Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Box className="MeetingCardCtas">
                          {item.meetingType !== 'InPerson' && actionFlag === 1 ? (
                            <Box className="MeetingCardActionButtons">
                              {rejectCancelCallButton(item, 'page')}
                              {joinCallButton(item, 'page')}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                              {!isAdmin && item.pendingReview === true && (
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    className="MeetingCardDetailsBtn"
                                    onClick={() => handleOpenReviewPopup(item.meetingId)}
                                  >
                                    Review
                                  </Button>
                                  {!hasReview && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: -2,
                                        right: -2,
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#FF3B30'
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                              <Button
                                variant="contained"
                                color="primary"
                                className="MeetingCardDetailsBtn"
                                onClick={handleDetailsClick}
                              >
                                Details
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
          </Box>

        </Box>

        {/* Rejection Reason modal */}
        {openRejectionCall && (
          <RejectionCallModal
            openRejectionCall={openRejectionCall}
            closeRejectionModal={closeRejectionModal}
            callMeetingId={callMeetingId}
            onRejectSuccess={handleCallRejection}
          />
        )}

        {/* Cancelation Reason modal */}
        {openCancelationCall && (
          <CancelationCallModal
            openCancelationCall={openCancelationCall}
            closeCancelModal={closeCancelModal}
            callMeetingId={callMeetingId}
            onCancelSuccess={handleCallCancellation}
          />
        )}

        {/* See Booking Details Modal */}
        {openCallbookingDetailsModal && (
          <SeeBookingDetailsModal
            openCallbookingDetailsModal={openCallbookingDetailsModal}
            closeSeeBookingDetailsModal={closeSeeBookingDetailsModal}
            bookedCallDetails={bookedCallDetails[0]}
            allattendeeDetails={allattendeeDetails}
            timezone={timezone}
            ModalDialogActions={<></>}
            userCallsDataRefetch={userCallsDataRefetch}
            isCallRejectedByAuthUser={true}
            setIsCallEditing={setIsCallEditing}
            isCallEditing={isCallEditing}
          />
        )}
        {((reviewMeetingId && reviewRow) || filteredCallRows.length > 0) && (
          <PostCallPopup
            key={reviewMeetingId || currentMeetingIndex}
            reviewParticipant={
              reviewMeetingId
                ? callRows.find((row) => row.meetingId === reviewMeetingId || row._id === reviewMeetingId)
                    ?.participantsUser
                : filteredCallRows[currentMeetingIndex].participantsUser
            }
            openPostCallPopup={openPostCallPopup}
            handleClosePostCallPopup={reviewMeetingId ? handleCloseReviewPopup : openNextPopup}
            meetingId={
              reviewMeetingId
                ? (callRows.find((row) => row.meetingId === reviewMeetingId || row._id === reviewMeetingId)
                    ?.meetingId || '')
                : filteredCallRows[currentMeetingIndex].meetingId
            }
            title={`Please review your meeting with\n${callWithTitle(
              reviewMeetingId
                ? callRows.find((row) => row.meetingId === reviewMeetingId || row._id === reviewMeetingId)
                : filteredCallRows[currentMeetingIndex]
            )}`}
            name={callWithTitle(
              reviewMeetingId
                ? callRows.find((row) => row.meetingId === reviewMeetingId || row._id === reviewMeetingId)
                : filteredCallRows[currentMeetingIndex]
            )}
          />
        )}

        {/* Call Booking Modal */}
        <CallBookingModal
          handleCloseModal={() => setCalendarOpen(false)}
          openFlag={isCalendarOpen}
          chatProfileName={''}
          threadParticipantsUsers={[]}
          chatType="Chats"
          pageName="Calendar"
          bookedCallDetails={''}
        />
      </Box>
    </Box>
  );
};

export default Calendar;
